/* Manage the state of the Layout component using the State Reducer pattern */

import { useMemo, useReducer } from 'react';
import { find, intersection, isEqual, isNumber, max, omit, range } from 'lodash';

import { getLogger } from '../../../utilities/UseLog';

const log = getLogger('LayoutReducer');

export const ROW_HEIGHT = 1;

const MAIN_LAYOUT = 'mainLayout';
const MAXIMIZED_PANEL_LAYOUT = 'maximizedPanelLayout';

const ACTIONS = {
    SET_PANELS: 'set-panels',
    ADD_PANEL: 'add-panel',
    REMOVE_PANEL: 'remove-panel',
    RESIZE_FLEX_PANELS: 'resize-flex-panels',
    SET_PANEL_HEIGHT: 'set-panel-height',
    UPDATE_LAYOUT: 'update-layout',
    MAXIMIZE_PANEL: 'maximize-panel',
    RESTORE_PANEL: 'restore-panel',
    RESIZE_MAXIMIZED_PANEL: 'resize-maximized-panel',
    SET_PANEL_AUTO_HEIGHT: 'set-panel-auto-height',
};

const ActionHandlers = {
    [ACTIONS.SET_PANELS]: setPanelsAction,
    [ACTIONS.ADD_PANEL]: addPanelAction,
    [ACTIONS.REMOVE_PANEL]: removePanelAction,
    [ACTIONS.RESIZE_FLEX_PANELS]: resizeFlexPanelsAction,
    [ACTIONS.SET_PANEL_HEIGHT]: setPanelHeightAction,
    [ACTIONS.UPDATE_LAYOUT]: updateLayoutAction,
    [ACTIONS.MAXIMIZE_PANEL]: maximizePanelAction,
    [ACTIONS.RESTORE_PANEL]: restorePanelAction,
    [ACTIONS.RESIZE_MAXIMIZED_PANEL]: resizeMaximizedPanelAction,
    [ACTIONS.SET_PANEL_AUTO_HEIGHT]: setPanelAutoHeightAction,
};

export const FULL_WIDTH = 12;
export const DEFAULT_PANEL_WIDTH = FULL_WIDTH;
export const DEFAULT_MIN_PANEL_WIDTH = 3;
export const DEFAULT_MIN_PANEL_HEIGHT = 150;

function initializeState(rowHeight) {
    return {
        rowHeight,
        [MAIN_LAYOUT]: [],
        [MAXIMIZED_PANEL_LAYOUT]: [],
        activeLayout: MAIN_LAYOUT,
        panels: [],
        panelLayoutOptions: {},
    };
}

export const DROPPING_ELEMENT_ID = '__dropping-elem__';
export const DROPPING_ELEMENT_WIDTH = FULL_WIDTH;
export const DROPPING_ELEMENT_HEIGHT = 15;
export const DEFAULT_DROPPING_ITEM = {
    i: DROPPING_ELEMENT_ID,
    w: DROPPING_ELEMENT_WIDTH,
    h: DROPPING_ELEMENT_HEIGHT,
};

export function pixelsToRows(pixels, rowHeight) {
    return Math.ceil(pixels / rowHeight);
}

export function rowsToPixels(rows, rowHeight) {
    return rows * rowHeight;
}

function panelsChanged(currentPanelIds, newPanelIds) {
    const numCurrent = currentPanelIds.length;
    const numNew = newPanelIds.length;

    // This is a relatively naive check, since it doesn't account for panel positions.  However, we
    // don't currently have a situation where panel positions change, so the check doesn't need to
    // be any more sophisticated.
    return numCurrent !== numNew || numCurrent !== intersection(currentPanelIds, newPanelIds).length;
}

function setPanelsAction(state, { optimize, panels, layoutOptionsProvider }) {
    const currentPanelIds = Object.keys(state.panelLayoutOptions);
    const newPanelIds = panels.map((panel) => panel.props.id);

    // If the panels didn't actually change, don't rebuild the layout.
    if (optimize && !panelsChanged(currentPanelIds, newPanelIds)) return { ...state, panels };

    // Cursors are used to position panels that do not have x or y positions defined in their
    // layout options, which is typically only panels from legacy dashboards.
    const yCursors = range(0, 12, 0);
    let xCursor = 0;
    const panelLayoutOptions = {};

    log.debug('Setting panels');

    const newLayout = panels.map((panel) => {
        const { id: panelId } = panel.props;
        const layoutOptions = layoutOptionsProvider(panel);
        let { x, y } = layoutOptions;
        const {
            w = DEFAULT_PANEL_WIDTH,
            minW = DEFAULT_MIN_PANEL_WIDTH,
            h = DEFAULT_MIN_PANEL_HEIGHT,
            minH = DEFAULT_MIN_PANEL_HEIGHT,
            disableVerticalResize = false,
        } = layoutOptions;

        log.debug(`Layout options for ${panelId}`);
        log.debugTable(layoutOptions);

        panelLayoutOptions[panelId] = layoutOptions;

        // Use the current x cursor if the panel does not have an x position.
        if (x === undefined) {
            if (xCursor + w > 12) xCursor = 0;
            x = xCursor;
        }

        // Use the current y cursor if the panel does not have a y position.
        if (y === undefined) {
            y = max(yCursors.slice(x, x + w));
        }

        // Update the cursors for the new layout item.
        const xStart = xCursor;
        xCursor += w;
        for (let col = xStart; col < xCursor; col++) {
            yCursors[col] = y + h;
        }

        log.debug(`Panel layout for ${panelId}`);
        log.debugTable({
            x,
            y,
            w,
            minW,
            h,
            minH,
        });

        log.debug(`Current x cursor: ${xCursor}`);
        log.debug('Current y cursors:');
        log.debugTable(yCursors);

        const calculatedMinH = pixelsToRows(minH, state.rowHeight);
        const calculatedH = pixelsToRows(isNumber(h) ? h : minH, state.rowHeight);

        return {
            i: panelId,
            x,
            y: pixelsToRows(y, state.rowHeight),
            w,
            minW,
            maxW: 12,
            h: Math.max(calculatedH, calculatedMinH),
            minH: calculatedMinH,
            resizeHandles: disableVerticalResize ? ['e'] : ['s', 'se', 'e'],
        };
    });

    log.debugTable(newLayout);

    return {
        ...state,
        [MAIN_LAYOUT]: newLayout,
        panels,
        panelLayoutOptions,
    };
}

function addPanelAction(state, { newPanel, newLayout, layoutOptions }) {
    const panelId = newPanel.props.id;

    log.debug(`Adding panel ${panelId}`);
    log.debugTable(layoutOptions);

    return {
        ...state,
        [MAIN_LAYOUT]: newLayout,
        panels: [...state.panels, newPanel],
        panelLayoutOptions: {
            ...state.panelLayoutOptions,
            [panelId]: layoutOptions,
        },
    };
}

function removePanelAction(state, { panelId }) {
    return {
        ...state,
        [MAIN_LAYOUT]: state[MAIN_LAYOUT].filter((layoutItem) => layoutItem.i !== panelId),
        panels: state.panels.filter((panel) => panel.props.id !== panelId),
        panelLayoutOptions: omit(state.panelLayoutOptions, panelId),
    };
}

function setPanelAutoHeightAction(state, { panelId, autoHeight }) {
    log.debug(`Panel ${panelId} autoHeight changed ${state.panelLayoutOptions[panelId].autoHeight} -> ${autoHeight}`);
    const panelLayoutOptions = {
        ...state.panelLayoutOptions,
        [panelId]: {
            ...state.panelLayoutOptions[panelId],
            autoHeight,
        },
    };

    return {
        ...state,
        panelLayoutOptions,
    };
}

function updateLayoutAction(state, { newLayout }) {
    const { activeLayout } = state;
    // Filter out the dropping element that gets produced when we're dragging a new element in:
    const newLayoutSansPlaceholder = newLayout.filter((layoutItem) => layoutItem.i !== DROPPING_ELEMENT_ID);

    if (!isEqual(state[activeLayout], newLayoutSansPlaceholder)) {
        log.debug(`Updating ${activeLayout} with layout of size ${newLayoutSansPlaceholder.length}`);
        log.debugTable(newLayoutSansPlaceholder);

        return {
            ...state,
            [activeLayout]: newLayoutSansPlaceholder,
        };
    }

    return state;
}

function resizeFlexPanelsAction(state, { containerSize, spacing }) {
    const layout = state[MAIN_LAYOUT];
    const updatedLayout = layout.map((layoutItem) => {
        const { flexHeight } = state.panelLayoutOptions[layoutItem.i];
        if (flexHeight) {
            return {
                ...layoutItem,
                h: Math.max(containerSize.height - layoutItem.y - spacing, layoutItem.minH),
            };
        }

        return layoutItem;
    });

    if (!isEqual(layout, updatedLayout)) {
        log.debug('Resized flex panels');
        log.debugTable(updatedLayout);
        return {
            ...state,
            [MAIN_LAYOUT]: updatedLayout,
        };
    }

    return state;
}

function setPanelHeightAction(state, { panelId, height }) {
    const { maximizedPanelId } = state;
    const layoutToUpdate = panelId === maximizedPanelId ? MAXIMIZED_PANEL_LAYOUT : MAIN_LAYOUT;
    const panelLayoutItem = find(state[layoutToUpdate], (layoutItem) => layoutItem.i === panelId);
    const h = Math.max(pixelsToRows(height, state.rowHeight), panelLayoutItem?.minH || 0);

    if (h !== panelLayoutItem.h) {
        log.debug(
            `Setting panel ${panelLayoutItem.i} height in ${layoutToUpdate} ${panelLayoutItem.h} -> ${h} (height: ${height} rowHeight: ${state.rowHeight})`
        );
        const updatedLayout = state[layoutToUpdate].map((layoutItem) => {
            if (layoutItem.i === panelLayoutItem.i) {
                return {
                    ...layoutItem,
                    h,
                };
            }

            return layoutItem;
        });

        log.debugTable(updatedLayout);

        return {
            ...state,
            [layoutToUpdate]: updatedLayout,
        };
    }

    return state;
}

function maximizePanelAction(state, { panelId, containerSize, spacing }) {
    log.debug(`Maximizing panel ${panelId}`);
    const fullLayout = state[MAIN_LAYOUT];
    const maximizedPanelLayout = fullLayout.map((layoutItem) => {
        if (layoutItem.i === panelId) {
            return {
                ...layoutItem,
                x: 0,
                y: 0,
                w: 12,
                h: Math.max(containerSize.height - spacing, layoutItem.minH),
            };
        }

        return {
            ...layoutItem,
            x: 0,
            y: 0,
            w: 0,
            h: 0,
            minW: 0,
            minH: 0,
        };
    });

    log.debugTable(maximizedPanelLayout);

    return {
        ...state,
        maximizedPanelId: panelId,
        [MAXIMIZED_PANEL_LAYOUT]: maximizedPanelLayout,
        activeLayout: MAXIMIZED_PANEL_LAYOUT,
    };
}

function restorePanelAction(state) {
    if (state.activeLayout === MAXIMIZED_PANEL_LAYOUT) {
        log.debug(`Restoring maximized panel`);
        return {
            ...state,
            maximizedPanelId: undefined,
            [MAXIMIZED_PANEL_LAYOUT]: [],
            activeLayout: MAIN_LAYOUT,
        };
    }

    return state;
}

function resizeMaximizedPanelAction(state, { containerSize, spacing }) {
    const layout = state[MAXIMIZED_PANEL_LAYOUT];
    const updatedLayout = layout.map((layoutItem) => {
        if (layoutItem.i === state.maximizedPanelId) {
            return {
                ...layoutItem,
                h: Math.max(containerSize.height - spacing, layoutItem.minH),
            };
        }

        return layoutItem;
    });

    if (!isEqual(layout, updatedLayout)) {
        log.debug('Resized maximized panel');
        log.debugTable(updatedLayout);
        return {
            ...state,
            [MAXIMIZED_PANEL_LAYOUT]: updatedLayout,
        };
    }

    return state;
}

function reducer(state, action) {
    const { type, params } = action;
    return ActionHandlers[type](state, params);
}

function runAction(dispatch, type, params) {
    dispatch({ type, params });
}

export default function useLayoutReducer(rowHeight) {
    const [state, dispatch] = useReducer(reducer, initializeState(rowHeight));
    const actions = useMemo(
        () => ({
            setPanels: (panels, layoutOptionsProvider, optimize) =>
                runAction(dispatch, ACTIONS.SET_PANELS, { panels, layoutOptionsProvider, optimize }),
            addPanel: (newPanel, newLayout, layoutOptions) =>
                runAction(dispatch, ACTIONS.ADD_PANEL, { newPanel, newLayout, layoutOptions }),
            removePanel: (panelId) => runAction(dispatch, ACTIONS.REMOVE_PANEL, { panelId }),
            updateLayout: (newLayout) => runAction(dispatch, ACTIONS.UPDATE_LAYOUT, { newLayout }),
            resizeFlexPanels: (containerSize, spacing) => {
                runAction(dispatch, ACTIONS.RESIZE_FLEX_PANELS, {
                    containerSize,
                    spacing,
                });
            },
            setPanelHeight: (panelId, height) => runAction(dispatch, ACTIONS.SET_PANEL_HEIGHT, { panelId, height }),
            maximizePanel: (panelId, containerSize, spacing) => {
                runAction(dispatch, ACTIONS.MAXIMIZE_PANEL, {
                    panelId,
                    containerSize,
                    spacing,
                });
            },
            restoreMaximizedPanel: () => runAction(dispatch, ACTIONS.RESTORE_PANEL),
            resizeMaximizedPanel: (containerSize, spacing) => {
                runAction(dispatch, ACTIONS.RESIZE_MAXIMIZED_PANEL, {
                    containerSize,
                    spacing,
                });
            },
            setPanelAutoHeight: (panelId, autoHeight) => {
                runAction(dispatch, ACTIONS.SET_PANEL_AUTO_HEIGHT, {
                    panelId,
                    autoHeight,
                });
            },
        }),
        []
    );

    return [state, actions];
}
